import { Engine } from './core/Engine';
import { InputManager } from './core/InputManager';
import { GameManager, GameMode } from './core/GameManager';
import { World } from './core/World';
import { Player } from './player/Player';
import { BuildSystem } from './building/BuildSystem';
import { HUD } from './ui/HUD';
import { LobbyUI } from './ui/LobbyUI';
import { BlockRegistry } from './voxel/BlockRegistry';
import { ObbyGame } from './games/obby';
import { TycoonGame } from './games/tycoon';

class Dublox {
  private engine: Engine;
  private inputManager: InputManager;
  private gameManager: GameManager;
  private lobbyUI: LobbyUI | null = null;

  // Sandbox mode components
  private world: World | null = null;
  private player: Player | null = null;
  private buildSystem: BuildSystem | null = null;
  private hud: HUD | null = null;
  private registry: BlockRegistry;

  // Obby mode components
  private obbyGame: ObbyGame | null = null;

  // Tycoon mode components
  private tycoonGame: TycoonGame | null = null;

  constructor() {
    this.engine = new Engine({
      canvasId: 'game-canvas',
      antialias: true,
      adaptToDeviceRatio: true,
    });

    this.inputManager = InputManager.getInstance();
    this.inputManager.attachToCanvas(this.engine.getCanvas());
    this.registry = BlockRegistry.getInstance();
    this.gameManager = GameManager.getInstance(this.engine);

    this.engine.setLoadProgressCallback(this.updateLoadingScreen.bind(this));
  }

  private updateLoadingScreen(progress: number, message: string): void {
    const loadingBar = document.getElementById('loading-bar');
    const loadingText = document.getElementById('loading-text');

    if (loadingBar) {
      loadingBar.style.width = `${progress}%`;
    }

    if (loadingText) {
      loadingText.textContent = message;
    }
  }

  private hideLoadingScreen(): void {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
      setTimeout(() => {
        loadingScreen.style.display = 'none';
      }, 500);
    }
  }

  public async start(): Promise<void> {
    console.log('🎮 Dublox - Iniciando...');

    try {
      await this.engine.initialize();

      this.engine.updateProgress(80, 'Preparando lobby...');

      // Create lobby UI
      this.lobbyUI = new LobbyUI();
      this.lobbyUI.onPlay((mode) => this.startGameMode(mode));

      this.engine.updateProgress(100, 'Pronto!');

      await new Promise(resolve => setTimeout(resolve, 300));

      this.hideLoadingScreen();
      this.engine.start();

      // Show lobby
      this.showLobby();

      console.log('✅ Dublox iniciado com sucesso!');
      console.log('🎮 Escolha um jogo no lobby para começar!');

    } catch (error) {
      console.error('❌ Erro ao iniciar Dublox:', error);
      throw error;
    }
  }

  private showLobby(): void {
    // Clean up any active game mode
    this.cleanupCurrentMode();

    this.gameManager.setMode(null);

    if (this.lobbyUI) {
      this.lobbyUI.show();
    }

    // Release pointer lock
    document.exitPointerLock();
  }

  private startGameMode(mode: GameMode): void {
    console.log(`🎮 Iniciando modo: ${mode}`);

    if (this.lobbyUI) {
      this.lobbyUI.hide();
    }

    this.gameManager.setMode(mode);

    switch (mode) {
      case 'sandbox':
        this.startSandboxMode();
        break;
      case 'obby':
        this.startObbyMode();
        break;
      case 'tycoon':
        this.startTycoonMode();
        break;
    }
  }

  private startSandboxMode(): void {
    console.log('🔨 Iniciando modo Sandbox...');

    // Create world
    this.world = new World(this.engine.getScene(), {
      name: 'Sandbox World',
      renderDistance: 4,
    });
    this.world.initialize();

    // Create player
    const spawnPoint = this.world.getSpawnPoint();
    this.player = new Player(
      this.engine.getScene(),
      this.world,
      this.engine.getCanvas(),
      { spawnPosition: spawnPoint }
    );

    // Create build system
    this.buildSystem = new BuildSystem(this.world);

    // Create HUD
    this.hud = new HUD();

    // Start game loop for sandbox
    this.startSandboxGameLoop();

    console.log('✅ Modo Sandbox iniciado!');
    console.log('   - Clique para capturar o mouse');
    console.log('   - WASD: Mover | Espaço: Pular');
    console.log('   - Botões do mouse: Construir/Remover blocos');
    console.log('   - ESC: Voltar ao lobby');
  }

  private startObbyMode(): void {
    console.log('🏃 Iniciando modo Obby...');

    // Create obby game
    this.obbyGame = new ObbyGame(this.engine.getScene());
    this.obbyGame.initialize();

    // Create player for obby (without world - uses platform collision)
    const spawnPoint = this.obbyGame.getSpawnPoint();

    // Create player without voxel world (Obby uses platform collision)
    this.player = new Player(
      this.engine.getScene(),
      null,
      this.engine.getCanvas(),
      { spawnPosition: spawnPoint }
    );

    this.obbyGame.setPlayer(this.player);
    this.obbyGame.start();

    this.obbyGame.onExit(() => {
      this.showLobby();
    });

    this.obbyGame.onFinish((time, deaths) => {
      console.log(`🏆 Obby concluído! Tempo: ${time.toFixed(2)}s, Mortes: ${deaths}`);
    });

    // Start game loop for obby
    this.startObbyGameLoop();

    console.log('✅ Modo Obby iniciado!');
    console.log('   - Clique para capturar o mouse');
    console.log('   - WASD: Mover | Espaço: Pular');
    console.log('   - Chegue ao final sem cair!');
  }

  private startTycoonMode(): void {
    console.log('🏭 Iniciando modo Tycoon...');

    // Create tycoon game
    this.tycoonGame = new TycoonGame(this.engine.getScene());
    this.tycoonGame.initialize();
    this.tycoonGame.start();

    this.tycoonGame.onExit(() => {
      this.showLobby();
    });

    // Start game loop for tycoon
    this.startTycoonGameLoop();

    console.log('✅ Modo Tycoon iniciado!');
    console.log('   - Use o mouse para navegar');
    console.log('   - Compre máquinas na loja');
    console.log('   - Construa sua fábrica de doces!');
  }

  private startSandboxGameLoop(): void {
    const scene = this.engine.getScene();

    const observer = scene.onBeforeRenderObservable.add(() => {
      if (this.gameManager.getCurrentMode() !== 'sandbox') {
        scene.onBeforeRenderObservable.remove(observer);
        return;
      }

      const deltaTime = scene.getEngine().getDeltaTime() / 1000;

      if (this.world) {
        this.world.update(deltaTime);
      }

      if (this.player) {
        this.player.update(deltaTime);

        if (this.buildSystem) {
          this.buildSystem.update(
            this.player.getEyePosition(),
            this.player.getLookDirection(),
            this.player.getPosition()
          );
        }
      }

      this.updateSandboxHUD();
      this.handleSandboxInput();

      this.inputManager.update();
    });
  }

  private startObbyGameLoop(): void {
    const scene = this.engine.getScene();

    const observer = scene.onBeforeRenderObservable.add(() => {
      if (this.gameManager.getCurrentMode() !== 'obby') {
        scene.onBeforeRenderObservable.remove(observer);
        return;
      }

      const deltaTime = scene.getEngine().getDeltaTime() / 1000;

      if (this.player) {
        this.player.update(deltaTime);
      }

      if (this.obbyGame) {
        this.obbyGame.update(deltaTime);
        this.obbyGame.setPointerLocked(this.inputManager.isPointerLockedState());
      }

      this.handleObbyInput();

      this.inputManager.update();
    });
  }

  private startTycoonGameLoop(): void {
    const scene = this.engine.getScene();

    const observer = scene.onBeforeRenderObservable.add(() => {
      if (this.gameManager.getCurrentMode() !== 'tycoon') {
        scene.onBeforeRenderObservable.remove(observer);
        return;
      }

      const deltaTime = scene.getEngine().getDeltaTime() / 1000;

      if (this.tycoonGame) {
        this.tycoonGame.update(deltaTime);
      }

      this.handleTycoonInput();

      this.inputManager.update();
    });
  }

  private updateSandboxHUD(): void {
    if (!this.hud || !this.buildSystem) return;

    this.hud.setPointerLocked(this.inputManager.isPointerLockedState());

    this.hud.updateHotbar(
      this.buildSystem.getHotbar(),
      this.buildSystem.getSelectedSlot()
    );

    if (this.hud.isDebugShowing() && this.player && this.world) {
      const pos = this.player.getPosition();
      const hit = this.buildSystem.getCurrentHit();

      this.hud.updateDebug({
        fps: this.engine.getBabylonEngine().getFps(),
        position: { x: pos.x, y: pos.y, z: pos.z },
        chunks: this.world.getStats().loadedChunks,
        meshes: this.engine.getScene().getActiveMeshes().length,
        cameraMode: this.player.getCameraMode(),
        grounded: this.player.isGrounded(),
        blockLooking: hit ? this.registry.get(hit.blockId)?.displayName : undefined,
      });
    }
  }

  private handleSandboxInput(): void {
    // Toggle debug with F3 or Backquote (`)
    if (this.inputManager.isKeyPressed('F3') || this.inputManager.isKeyPressed('Backquote')) {
      if (this.hud) {
        this.hud.toggleDebug();
      }
    }

    // Toggle fullscreen with F11
    if (this.inputManager.isKeyPressed('F11')) {
      this.toggleFullscreen();
    }

    // Reload chunks with F5
    if (this.inputManager.isKeyPressed('F5')) {
      if (this.world) {
        console.log('🔄 Recarregando chunks...');
        this.world.getChunkManager().forceReloadAllChunks();
      }
    }

    // Respawn with R
    if (this.inputManager.isKeyPressed('KeyR')) {
      if (this.world && this.player) {
        const spawn = this.world.getSpawnPoint();
        this.player.setPosition(spawn);
        console.log('🔄 Respawn no ponto inicial');
      }
    }

    // Return to lobby with ESC (when pointer not locked)
    if (this.inputManager.isKeyPressed('Escape')) {
      if (!this.inputManager.isPointerLockedState()) {
        this.showLobby();
      }
    }
  }

  private handleObbyInput(): void {
    // Return to lobby with ESC (when pointer not locked)
    if (this.inputManager.isKeyPressed('Escape')) {
      if (!this.inputManager.isPointerLockedState()) {
        this.showLobby();
      }
    }
  }

  private handleTycoonInput(): void {
    // ESC always returns to lobby in tycoon (no pointer lock needed)
    if (this.inputManager.isKeyPressed('Escape')) {
      this.showLobby();
    }
  }

  private cleanupCurrentMode(): void {
    // Cleanup sandbox
    if (this.world) {
      this.world.dispose();
      this.world = null;
    }

    if (this.buildSystem) {
      this.buildSystem.dispose();
      this.buildSystem = null;
    }

    if (this.hud) {
      this.hud.dispose();
      this.hud = null;
    }

    // Cleanup obby
    if (this.obbyGame) {
      this.obbyGame.dispose();
      this.obbyGame = null;
    }

    // Cleanup tycoon
    if (this.tycoonGame) {
      this.tycoonGame.dispose();
      this.tycoonGame = null;
    }

    // Cleanup player
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  // Debug getters
  public getPlayer(): Player | null {
    return this.player;
  }

  public getWorld(): World | null {
    return this.world;
  }
}

// Start the game when DOM is ready
window.addEventListener('DOMContentLoaded', async () => {
  const game = new Dublox();
  await game.start();

  // Expose for debugging
  (window as unknown as { game: Dublox }).game = game;
});
